import { useState, FormEvent } from "react";
import { Link } from "react-router-dom";

const SITE_URL = import.meta.env.VITE_SITE_URL || '';

const rewards = [
  { icon: "ri-gift-line", title: "Refer a Friend", reward: "$20 Discount", desc: "Send a friend to any Cardiocraft CPR course and earn a $20 credit toward your next class." },
  { icon: "ri-group-line", title: "Refer a Group", reward: "Free Class", desc: "Refer a group of 5+ people and earn a complimentary individual course of your choice." },
  { icon: "ri-hospital-line", title: "Refer an Organization", reward: "Premium Credit", desc: "Connect us with a hospital, business, or school for group training and receive premium referral rewards." },
];

const steps = [
  { step: "01", title: "Share Your Code", desc: "Get your unique referral code from us and share it with friends, colleagues, or organizations." },
  { step: "02", title: "They Book a Class", desc: "Your referral books any Cardiocraft CPR course using your code — online or by phone." },
  { step: "03", title: "You Get Rewarded", desc: "Once they complete their class, your reward is automatically applied to your account." },
];

export default function ReferralPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLTextAreaElement;
      if (input.name) data.append(input.name, input.value);
    });
    try {
      await fetch("https://readdy.ai/api/form/d71gj9ghb0rqicn68he0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-20">
      <title>CPR Referral Program Kissimmee | Earn Rewards | Cardiocraft CPR</title>
      <meta name="description" content="Refer friends, colleagues, or organizations to Cardiocraft CPR in Kissimmee, FL and earn exclusive rewards. Get $20 discounts, free classes, or premium credits for every referral." />
      <meta name="keywords" content="CPR referral program Kissimmee, Cardiocraft CPR rewards, refer CPR class Florida, earn discount CPR training Kissimmee" />
      <link rel="canonical" href={`${SITE_URL}/referral`} />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'CPR Referral Program – Cardiocraft CPR Kissimmee',
            url: `${SITE_URL}/referral`,
            description: 'Refer friends or organizations to Cardiocraft CPR and earn rewards including discounts and free classes. Kissimmee, FL.',
            isPartOf: {
              '@type': 'WebSite',
              name: 'Cardiocraft CPR',
              url: SITE_URL,
            },
          }),
        }}
      />
      {/* Hero */}
      <section className="relative py-24 bg-brand-purple overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://public.readdy.ai/ai/img_res/4d27dd0eb3414880f7f92364f4e75e14.jpg"
            alt="CPR referral program Kissimmee"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <span className="inline-block border border-brand-gold/50 text-brand-gold text-xs font-bold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
            Earn Rewards
          </span>
          <h1 className="font-serif text-5xl lg:text-6xl font-bold text-white mb-6">
            Refer &amp; Get <span className="text-brand-gold">Rewarded</span>
          </h1>
          <p className="text-white/80 text-lg leading-relaxed mb-8">
            Know someone who needs CPR certification? Refer them to Cardiocraft CPR and
            earn exclusive discounts, free classes, and premium rewards — while spreading
            life-saving knowledge throughout Kissimmee.
          </p>
          <a
            href="#referral-form"
            className="inline-flex items-center gap-2 bg-brand-gold text-brand-black font-bold px-8 py-4 rounded-md hover:bg-brand-gold-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-gift-line" />
            Join the Referral Program
          </a>
        </div>
      </section>

      {/* Rewards */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-brand-black mb-4">
              Your Referral Rewards
            </h2>
            <div className="w-20 h-1 bg-brand-gold mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {rewards.map((reward) => (
              <div
                key={reward.title}
                className="bg-brand-purple-pale rounded-xl p-8 border border-brand-purple/10 text-center hover:-translate-y-1 transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-brand-purple rounded-xl text-white mx-auto mb-5">
                  <i className={`${reward.icon} text-2xl`} />
                </div>
                <h3 className="font-serif font-bold text-xl text-brand-black mb-2">{reward.title}</h3>
                <span className="inline-block bg-brand-gold text-brand-black text-sm font-bold px-4 py-1.5 rounded-full mb-4">
                  {reward.reward}
                </span>
                <p className="text-gray-500 text-sm leading-relaxed">{reward.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-brand-purple-pale">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl font-bold text-brand-black mb-4">
              How It Works
            </h2>
            <div className="w-20 h-1 bg-brand-gold mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div key={step.step} className="text-center">
                <div className="font-serif text-6xl font-bold text-brand-purple/20 mb-3">{step.step}</div>
                <h3 className="font-semibold text-brand-black text-lg mb-3">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Referral Form */}
      <section id="referral-form" className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-brand-purple-pale rounded-2xl p-8 lg:p-12 border border-brand-purple/10">
            <div className="text-center mb-8">
              <h2 className="font-serif text-3xl font-bold text-brand-black mb-2">
                Start Referring Today
              </h2>
              <p className="text-gray-500 text-sm">
                Fill out the form and we&apos;ll set up your referral code and send you everything you need.
              </p>
            </div>

            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center bg-brand-purple rounded-full mx-auto mb-4 text-white">
                  <i className="ri-gift-2-line text-3xl" />
                </div>
                <h3 className="font-semibold text-brand-black text-xl mb-2">You&apos;re In!</h3>
                <p className="text-gray-500 text-sm">We&apos;ll be in touch with your referral code shortly.</p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-brand-black mb-1.5">Your Name *</label>
                    <input
                      type="text"
                      name="referrer_name"
                      required
                      placeholder="Your full name"
                      className="w-full h-12 px-4 bg-white border-2 border-gray-200 rounded-md text-sm focus:border-brand-purple focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-brand-black mb-1.5">Your Email *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full h-12 px-4 bg-white border-2 border-gray-200 rounded-md text-sm focus:border-brand-purple focus:outline-none transition-colors"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-black mb-1.5">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(555) 000-0000"
                    className="w-full h-12 px-4 bg-white border-2 border-gray-200 rounded-md text-sm focus:border-brand-purple focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-brand-black mb-1.5">Who Are You Referring?</label>
                  <textarea
                    name="referral_info"
                    rows={3}
                    maxLength={500}
                    placeholder="Tell us about who you'd like to refer (individual, group, business name...)"
                    className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-md text-sm focus:border-brand-purple focus:outline-none transition-colors resize-none"
                  />
                  <p className="text-gray-400 text-xs mt-1">Max 500 characters</p>
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-brand-gold text-brand-black font-bold py-3.5 rounded-md hover:bg-brand-gold-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin" /> Submitting...</>
                  ) : (
                    <><i className="ri-gift-line" /> Get My Referral Code</>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-10 bg-gray-50 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs leading-relaxed">
            Referral rewards apply after referred student completes their paid class.
            Discounts cannot be combined with other offers. Contact us for full terms.
            Questions? Email <a href="mailto:cardiocraftcpr@gmail.com" className="text-brand-purple hover:underline">cardiocraftcpr@gmail.com</a>
          </p>
          <div className="mt-6">
            <Link to="/contact" className="text-brand-purple text-sm font-semibold hover:underline cursor-pointer">
              Questions? Contact Us
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
